import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';

interface PaginationProps {
  boundaryCount?: number;
  count?: number;
  currentPage?: number;
  disabled?: boolean;
  hideNextButton?: boolean;
  hidePrevButton?: boolean;
  onChange?: (event: ChangeEvent<unknown>, value: number) => void;
  page?: number | undefined;
  siblingCount?: number | boolean;
  maxLength?: number;
  [key: string]: unknown;
}

export default function usePagination(props: PaginationProps = {}) {
  const {
    boundaryCount = 1,
    count = 1,
    currentPage = 1,
    disabled = false,
    hideNextButton = false,
    hidePrevButton = false,
    onChange: handleChange,
    page: pageProp,
    siblingCount = 1,
    maxLength,
    ...other
  } = props;

  const isControlledRef = useRef(pageProp !== undefined);
  const [pageCurrent, setPageCurrent] = useState<number | undefined>(
    currentPage
  );
  const controlledPage =
    (isControlledRef.current ? pageProp : pageCurrent) ?? 1;

  const handleClick = (event: MouseEvent<unknown>, value: number): void => {
    if (!isControlledRef.current) {
      setPageCurrent(value);
    }
    if (handleChange) {
      handleChange(event, value);
    }
  };

  const range = (start: number, end: number): number[] => {
    const length = Math.round(end - start + 1);
    return Array.from({ length }, (_, i) => Math.round(start + i));
  };

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count
  );

  const shouldShowEllipsis = typeof siblingCount === 'number' ? true : false;

  let siblingsStart: number;
  let siblingsEnd: number;
  if (maxLength && maxLength > 0 && !siblingCount) {
    const visibleItems = Math.min(maxLength, count);
    const firstVisibleItem = Math.max(
      controlledPage - Math.floor(visibleItems / 2),
      1
    );

    siblingsStart = Math.min(firstVisibleItem, count - visibleItems + 1);
    siblingsEnd = Math.min(firstVisibleItem + visibleItems - 1, count);
  } else {
    siblingsStart = Math.max(
      Math.min(
        controlledPage - (shouldShowEllipsis ? (siblingCount as number) : 0),
        count -
          boundaryCount -
          (shouldShowEllipsis ? (siblingCount as number) * 2 : 0) -
          1
      ),
      boundaryCount + 2
    );

    siblingsEnd = Math.min(
      Math.max(
        controlledPage + (shouldShowEllipsis ? (siblingCount as number) : 0),
        boundaryCount +
          (shouldShowEllipsis ? (siblingCount as number) * 2 : 0) +
          2
      ),
      endPages.length > 0 ? endPages[0] - 2 : count - 1
    );
  }

  const itemList =
    maxLength && maxLength > 0 && !siblingCount
      ? [
          ...(hidePrevButton ? [] : ['voltar']),

          ...range(siblingsStart, siblingsEnd),
          ...(hideNextButton ? [] : ['próximo']),
        ]
      : [
          ...(hidePrevButton ? [] : ['voltar']),
          ...startPages,
          ...(shouldShowEllipsis
            ? siblingsStart > boundaryCount + 2
              ? ['start-ellipsis']
              : boundaryCount + 1 < count - boundaryCount
              ? [boundaryCount + 1]
              : []
            : range(boundaryCount + 1, siblingsStart - 1)),
          ...range(siblingsStart, siblingsEnd),
          ...(shouldShowEllipsis
            ? siblingsEnd < count - boundaryCount - 1
              ? ['end-ellipsis']
              : count - boundaryCount > boundaryCount
              ? [count - boundaryCount]
              : []
            : range(siblingsEnd + 1, count - boundaryCount)),
          ...endPages,
          ...(hideNextButton ? [] : ['próximo']),
        ];

  const buttonPage = (type: string): number | null => {
    switch (type) {
      case 'voltar':
        return controlledPage - 1;
      case 'próximo':
        return controlledPage + 1;
      default:
        return null;
    }
  };

  const items = itemList.map((item) => {
    return typeof item === 'number'
      ? {
          onClick: (event: MouseEvent<unknown>): void => {
            handleClick(event, item);
          },
          type: 'page',
          page: item,
          selected: item === controlledPage,
          disabled,
          'aria-current': item === controlledPage ? 'true' : undefined,
        }
      : {
          onClick: (event: MouseEvent<unknown>): void => {
            const pageValue = buttonPage(item);
            if (pageValue !== null) {
              handleClick(event, pageValue);
            }
          },
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            disabled ||
            (item.indexOf('ellipsis') === -1 &&
              (item === 'próximo'
                ? controlledPage >= count
                : controlledPage <= 1)),
        };
  });

  useEffect(() => {
    setPageCurrent(currentPage);
  }, [currentPage]);

  return {
    items,
    pageCurrent,
    ...other,
  };
}
